'use client';

import { styled } from 'styled-components';
import TextLink from '@dvd/components/TextLink';
import Media from '@dvd/components/Media';
import cs from 'classnames';
import type { IconNameType } from '@wistia/ui';
import FeatureCard from '@dvd/components/FeatureCard';
import * as Styled from '@lib/styles';

type FeatureProps = Record<
  string,
  {
    headline: string;
    description: string;
    href: string;
    icon: IconNameType;
  }
>;

export const FeaturesLibrary = {
  hosting: {
    headline: 'Hosting',
    description:
      'Your home for top-notch video playback, analytics, and organization.',
    href: '/product/hosting',
    icon: 'home',
  },
  interactive_video: {
    headline: 'Engagement',
    description: 'Key features that turn passive viewers into active ones.',
    href: '/product/interactive-video',
    icon: 'integrations',
  },
  record: {
    headline: 'Recording',
    description: 'Create super polished videos with just your laptop.',
    href: '/product/video-recording',
    icon: 'record',
  },
  video_management: {
    headline: 'Management',
    description: 'Store, organize, find, and archive your videos in a jiffy.',
    href: '/product/video-management',
    icon: 'project',
  },
  player: {
    headline: 'Playback',
    description:
      'We designed our player to be fast, customizable, and compatible with any browser or device.',
    href: '/product/player',
    icon: 'play',
  },
  channels: {
    headline: 'Video galleries',
    description:
      'Build an on-brand page for all your content. No coding required.',
    href: '/product/channels',
    icon: 'channel',
  },
  lead_generation: {
    headline: 'Lead generation',
    description: 'Turn your videos into marketing machines.',
    href: '/product/lead-generation',
    icon: 'private-user-sessions',
  },
  live_streaming: {
    headline: 'Webinars',
    description:
      'Hosting webinars is now a piece of cake. Preferably red velvet.',
    href: '/product/webinar-hosting',
    icon: 'live',
  },
  analytics: {
    headline: 'Video analytics',
    description:
      'Get nerdy with key metrics, heatmaps, and A/B testing at your fingertips.',
    href: '/product/analytics',
    icon: 'stats',
  },
  podcasting: {
    headline: 'Podcasting',
    description:
      'Upload your pod and we’ll distribute it to top directories like Apple and Spotify.',
    href: '/product/podcasting',
    icon: 'mic',
  },
  video_seo: {
    headline: 'SEO',
    description:
      'We automatically optimize your videos for search to get you more views.',
    href: '/product/video-seo',
    icon: 'search',
  },
  marketing_automation: {
    headline: 'Marketing integrations',
    description:
      'Automatically send video data to your favorite marketing tools.',
    href: '/product/marketing-automation',
    icon: 'integrations',
  },
  video_editing: {
    headline: 'Editing',
    description: 'Zhuzhing up your videos has never been easier.',
    href: '/product/video-editing',
    icon: 'scissors',
  },
  collaboration: {
    headline: 'Collaboration',
    description: 'We’ve made getting feedback simple and fun. Really!',
    href: '/product/collaboration',
    icon: 'thumbs-up',
  },
  embeds: {
    headline: 'Embedding',
    description:
      'Copy and paste our embed code onto your page for reliable playback on any device.',
    href: '/product/embeds',
    icon: 'media',
  },
  zoom: {
    headline: 'Zoom integration',
    description:
      'Zoom videos auto-import into Wistia as soon as you finish recording.',
    href: '/integrations/recording/zoom',
    icon: 'integrations',
  },
  youtube: {
    headline: 'YouTube integration',
    description:
      'Combine Wistia’s world-class hosting with YouTube’s discoverability and reach.',
    href: '/integrations/social-media/youtube',
    icon: 'integrations',
  },
  platform: {
    headline: 'Wistia API',
    description: 'Build custom video experiences for your site.',
    href: '/product/platform',
    icon: 'embed',
  },
  localization: {
    headline: 'Localization',
    description: 'Dub and translate your videos in 30+ languages.',
    href: '/product/localize',
    icon: 'localization',
  },
} satisfies FeatureProps;

type Feature = keyof typeof FeaturesLibrary;

type FeatureListProps = {
  /**
   * Eyebrow sub-headline
   */
  eyebrowText?: string;
  /**
   * What the H3 element says
   */
  headline: string;
  /**
   * Descriptive paragraph
   */
  description: string;
  /**
   * Which FeatureCards do you want to display? (Requires at least 7.)
   */
  features: [
    Feature,
    Feature,
    Feature,
    Feature,
    Feature,
    Feature,
    Feature,
    ...Feature[],
  ];
  /**
   * What href should the button/link point to?
   */
  href?: string;
  /**
   * What the link should say.
   */
  ctaText?: string;
  /**
   * The hashed id of the video; nesc. for showing video
   */
  hashedId?: string;
  /**
   * URL of image; nesc. for showing image
   */
  imageSrc?: string;
  /**
   * Alt text for image; nesc. for showing image
   */
  imageAlt?: string;
  /**
   *  Height of image; nesc. for showing image
   */
  imageHeight?: number;
  /**
   *  Width of image; nesc. for showing image
   */
  imageWidth?: number;
};

const FeatureListContainer = styled.div`
  margin: ${({ theme }) => `${theme.spacing[2]} 0`};

  .textLink {
    display: block;
    text-align: center;
    margin-top: ${({ theme }) => theme.spacing[2]};
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    margin: ${({ theme }) => `${theme.spacing[4]} 0`};
  }
`;

const FeatureListHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  text-align: center;
`;

const FeatureListGrid = styled.div`
  display: grid;
  margin-block: ${({ theme }) => theme.spacing[2]};
  ${({ theme }) => theme.grid.columns.small};

  .featureMedia {
    display: none;
  }

  .featureCards {
    display: grid;
    grid-column: auto / span 2;
    grid-template-columns: subgrid;
    row-gap: ${({ theme }) => theme.spacing[2]};
    align-items: start;

    & > * {
      grid-column: auto / span 2;
    }
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    ${({ theme }) => theme.grid.columns.medium};
    .featureCards {
      grid-column: auto / span 6;

      & > * {
        grid-column: auto / span 3;
      }
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    margin-block: ${({ theme }) => theme.spacing[3]};

    ${({ theme }) => theme.grid.columns.large};
    .featureCards {
      grid-column: auto / span 12;

      & > * {
        grid-column: auto / span 6;
      }
    }
  }

  ${({ theme }) => theme.mq.minWidth.xl} {
    margin-block: ${({ theme }) => theme.spacing[4]};

    .featureCards {
      grid-column: auto / span 12;

      & > * {
        grid-column: auto / span 4;
      }
    }

    &.withMedia {
      .featureMedia {
        display: block;
        grid-column: auto / span 4;
        margin: 0;
      }

      .featureCards {
        align-self: start;
        grid-column: auto / span 8;
      }
    }
  }
`;

const FeatureList = ({
  features,
  ctaText = 'Learn more',
  description,
  eyebrowText,
  headline,
  href,
  hashedId,
  imageAlt,
  imageHeight,
  imageSrc,
  imageWidth,
}: FeatureListProps) => {
  const hasMedia = Boolean(hashedId || imageSrc);
  const featureCards = features.map((title) => FeaturesLibrary[title]);

  return (
    <FeatureListContainer>
      <FeatureListHeader>
        {eyebrowText ? <Styled.Eyebrow>{eyebrowText}</Styled.Eyebrow> : null}
        <h2>{headline}</h2>
        <p>{description}</p>
      </FeatureListHeader>
      <FeatureListGrid className={cs(hasMedia ? 'withMedia' : null)}>
        {hasMedia ? (
          <div className="featureMedia">
            {hashedId ? <Media hashedId={hashedId} /> : null}
            {imageSrc ? (
              <Media
                alt={imageAlt}
                height={imageHeight}
                src={imageSrc}
                width={imageWidth}
              />
            ) : null}
          </div>
        ) : null}
        <div className="featureCards">
          {featureCards.map((feature) => (
            <FeatureCard key={feature.headline} size="sd" {...feature} />
          ))}
        </div>
      </FeatureListGrid>
      {href ? (
        <TextLink className="textLink" href={href}>
          {ctaText}
        </TextLink>
      ) : null}
    </FeatureListContainer>
  );
};

export default FeatureList;
